import type { ReactElement, ReactNode } from 'react';

/* A single figure with its label, and an honest way to say there is no figure.
 *
 * **`value` is `number | string | null`, and `null` is the load-bearing case.** Every dashboard in
 * this product can be looked at by a tenant for whom the underlying thing has never run, and the
 * dangerous rendering is not a missing card — it is a `0` or a large `—` where a measurement would
 * be. This platform holds absence-is-not-zero at ten sites in its own domain logic (a freshness read
 * of `None` is not a breach, an empty relation's completeness is `null` rather than `1.0`), and the
 * UI is where that care is most easily thrown away. So `null` renders the `absent` sentence at body
 * size in muted text, never a glyph at display size.
 *
 * `caption` is required whenever `value` is `null`: an unavailable metric that does not say *why* is
 * indistinguishable from a broken screen, which is the state this component exists to prevent.
 */

export interface MetricCardProps {
  readonly label: string;
  /** `null` means "not measured" and must be accompanied by `absent`. */
  readonly value: number | string | null;
  /** The words shown in place of a missing figure. Required by the type when `value` is null. */
  readonly absent?: string;
  /** Units or a qualifier, rendered beside the figure at body size. */
  readonly unit?: string | undefined;
  /** One line under the figure: a trend, a denominator, or why it is unavailable. */
  readonly caption?: ReactNode;
  /** A rule for the domain this figure belongs to, drawn on the card's top edge.
   *
   * All four domains, where this took three: `security` existed as a token and as a
   * `--domain-security` hue and could not be asked for here, so the one domain a compliance
   * reader most needs to spot was the one a metric card could not mark. */
  /* A restatement of the figure as a proportion. `percent` is clamped rather than trusted, because
   * a score is 0-100 by contract and a bar wider than its track is a rendering bug that looks like
   * data. `label` is required: a `role="img"` with no accessible name is an unlabelled graphic, and
   * the bar carries information the number alone does not — where it sits against the scale. */
  readonly bar?: { readonly percent: number; readonly label: string };
  /** A richer restatement than `bar` can express: a trend line, a severity split, a coverage bar
   * with a hatched remainder.
   *
   * **Additive rather than a replacement for `bar`**, and deliberately so: `bar` is a single
   * clamped percentage with an enforced label, which is the right shape for the common case and is
   * used on several screens. Widening it into "any node" would have thrown away that clamp and that
   * required label for every existing caller. A card may pass either; passing both would stack two
   * restatements of one figure, so the render draws `visual` and drops `bar` when both arrive. */
  readonly visual?: ReactNode | undefined;
  readonly accent?: 'quality' | 'governance' | 'ai' | 'security' | undefined;
}

export function MetricCard({
  label,
  value,
  absent,
  unit,
  caption,
  accent,
  bar,
  visual,
}: MetricCardProps): ReactElement {
  const missing = value === null;
  return (
    /* A class per domain rather than an inline style, and **on the top edge rather than the
       left**. Three things were wrong with the inline version: the rule was on the left, where
       the design spec puts it on top for a metric card (a figure is read from its left edge, so a
       rule there competes with the number); it set `borderRadius: 0`, which squared off the card's
       corners as a side effect of adding a domain marker; and it built a token name by string
       concatenation, so a typo in `accent` produced an unresolvable `var()` and silently no rule at
       all. */
    <div
      className={
        accent === undefined
          ? 'lq-metric-card'
          : `lq-metric-card lq-metric-card--${accent}`
      }
    >
      <span className="lq-metric-card__label">{label}</span>
      <span
        className={
          missing
            ? 'lq-metric-card__value lq-metric-card__value--absent'
            : 'lq-metric-card__value'
        }
      >
        {missing ? (absent ?? 'not measured') : value}
        {!missing && unit !== undefined ? (
          <span className="lq-metric-card__unit">{unit}</span>
        ) : null}
      </span>

      {/* The bar is *after* the figure and before the caption, as the reference has it: it restates
          the number as a shape, so it has to follow the number to read as a restatement rather than
          as a separate measurement.

          Rendered only when there is a value. A track with no fill beside an absent figure would be
          a zero-width bar, and a zero-width fill and a measured zero are the same picture — the
          absence rule this codebase holds at ten sites. */}
      {/* Rendered only when there is a value, for `bar`'s reason one branch down: a graphic beside an
          absent figure is a shape with nothing behind it, and an empty track and a measured zero are
          the same picture. */}
      {!missing && visual !== undefined ? visual : null}
      {!missing && visual === undefined && bar !== undefined ? (
        <span className="lq-metric-card__bar" role="img" aria-label={bar.label}>
          <span
            className="lq-metric-card__bar-fill"
            // The only genuinely computed value on the card.
            style={{ width: `${String(Math.max(0, Math.min(100, bar.percent)))}%` }}
          />
        </span>
      ) : null}
      {caption !== undefined ? (
        <span className="lq-metric-card__foot">{caption}</span>
      ) : null}
    </div>
  );
}

export interface ScoreBarProps {
  readonly name: string;
  /** 0..1, or `null` when the dimension was not measured. */
  readonly ratio: number | null;
  /** What to show instead of a percentage when `ratio` is null. */
  readonly absent?: string;
  /** Overrides the tone. By default the bar is thresholded — see `toneFor`. */
  readonly tone?: 'ok' | 'warn' | 'danger' | undefined;
}

/* Thresholds, and they are a product decision rather than an aesthetic one. Below 0.7 is red because
 * a dimension failing three records in ten is not a warning; 0.7–0.9 is amber; above 0.9 is green.
 * Stated here in one place so two screens cannot disagree about what a good score looks like — which
 * is the same reason `coverageOf` and `uncoveredKinds` are exported from one feature rather than
 * re-derived. */
const GOOD = 0.9;
const POOR = 0.7;

function toneFor(ratio: number): 'ok' | 'warn' | 'danger' {
  if (ratio >= GOOD) return 'ok';
  if (ratio >= POOR) return 'warn';
  return 'danger';
}

const BAR_COLOUR = {
  ok: 'var(--status-ok)',
  warn: 'var(--status-warn)',
  danger: 'var(--status-error)',
} as const;

export function ScoreBar({ name, ratio, absent, tone }: ScoreBarProps): ReactElement {
  const missing = ratio === null;
  const resolved = missing ? undefined : (tone ?? toneFor(ratio));
  return (
    <div className="lq-scorebar">
      <div className="lq-scorebar__head">
        <span className="lq-scorebar__name">{name}</span>
        <span className="lq-scorebar__value">
          {missing ? (absent ?? 'not measured') : `${(ratio * 100).toFixed(0)}%`}
        </span>
      </div>
      {/* A hatched track and no fill when unmeasured, because a zero-width fill and a measured zero
          are the same picture — and this product's own profiler goes out of its way to keep an
          all-null column (a measured 0.0) distinct from an empty relation (unmeasured). */}
      <div
        className={
          missing ? 'lq-scorebar__track lq-scorebar__track--absent' : 'lq-scorebar__track'
        }
      >
        {missing ? null : (
          <div
            className="lq-scorebar__fill"
            style={{
              width: `${String(Math.min(100, Math.max(0, ratio * 100)))}%`,
              ['--scorebar-colour' as string]: BAR_COLOUR[resolved ?? 'ok'],
            }}
            role="progressbar"
            aria-valuenow={Math.round(ratio * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={name}
          />
        )}
      </div>
    </div>
  );
}
